import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { validate, res } from 'react-email-validator';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import Backdrop from '@mui/material/Backdrop';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import PhoneInput from 'react-phone-input-2';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import EditIcon from '@mui/icons-material/Edit';
import TicketService from '@Helpers/URLHelpers/KaiziqResolve/TicketService';
import ErrorHandler from '@Root/ErrorHandler';
import 'react-phone-input-2/lib/material.css';
import './UpdateReportersForm.scss';

export default function UpdateReportersForm(props) {
    const [cursor, setCursor] = useState('auto');
    const [isValidMail, setIsValidMail] = useState(true);
    const [isFieldUpdated, setIsFieldUpdated] = useState(false);
    const [reporterUser, setReporterUser] = useState();
    const [errors, setErrors] = useState({});
    
    const ticketService = new TicketService();
    const errorHandler = new ErrorHandler();
    const className = 'updateReportersForm';

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        let reporterUserObj = {
            ticketId: props?.ticket?.id,
            createdByFullName: props?.ticket?.createdByFullName,
            createdByEmail: props?.ticket?.createdByEmail,
            createdByContactNumber: props?.ticket?.createdByContactNumber
        };
        setReporterUser(reporterUserObj);
    };

    const onPhoneNumberChange = (newNumber) => {
        setReporterUser((reporter) => ({ ...reporter, createdByContactNumber: newNumber }));
        setIsFieldUpdated(true);
        setErrors((prev) => ({ ...prev, phone: false }));
    };

    const handleChangeValue = async (e, type) => {
        let sanitizedValue = DOMPurify.sanitize(e.target.value);
        setReporterUser((reporter) => ({ ...reporter, [type]: sanitizedValue }));
        setIsFieldUpdated(true);
        setErrors((prev) => ({ ...prev, [type]: false }));
        
        if (type === 'createdByEmail') {
            setIsValidMail(true);
        }
    };

    const emailValidCheck = (email) => {
        validate(email);
        return res;
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!reporterUser?.createdByFullName?.trim()) {
            newErrors.createdByFullName = 'Full name is required';
        }
        
        if (!reporterUser?.createdByEmail?.trim()) {
            newErrors.createdByEmail = 'Email is required';
        } else if (!emailValidCheck(reporterUser.createdByEmail)) {
            newErrors.createdByEmail = 'Please enter a valid email';
        }
        
        if (!reporterUser?.createdByContactNumber?.trim()) {
            newErrors.phone = 'Contact number is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setCursor('wait');
        try {
            let checkEmail = emailValidCheck(reporterUser.createdByEmail);
            setIsValidMail(checkEmail);
            
            if (checkEmail && isFieldUpdated) {
                let response = await ticketService.updateTicketReporter(reporterUser);
                if (response.status == 200) {
                    await props.renderData();
                    props.closePopup();
                }
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}
            style={{ cursor: cursor }}
            className={className}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    timeout: 500,
                    sx: { backgroundColor: 'rgba(0, 0, 0, 0.6)' }
                }
            }}
        >
            <Fade in={props.open}>
                <Box className={className + '__container'}>
                    <Paper className={className + '__paper'} elevation={24}>
                        {/* Header */}
                        <Box className={className + '__header'}>
                            <Box className={className + '__headerContent'}>
                                <Box className={className + '__iconWrapper'}>
                                    <EditIcon className={className + '__headerIcon'} />
                                </Box>
                                <Box>
                                    <Typography className={className + '__title'}>
                                        Update Reporter Details
                                    </Typography>
                                    <Typography className={className + '__subtitle'}>
                                        Modify the contact information for this ticket
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                onClick={props.closePopup}
                                className={className + '__closeBtn'}
                                size="small"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Divider />

                        {/* Body */}
                        <Box className={className + '__body'} component="form" onSubmit={handleSubmit}>
                            {!isFieldUpdated && (
                                <Alert severity="info" className={className + '__infoAlert'}>
                                    Make changes to the reporter's information and click Update.
                                </Alert>
                            )}

                            <Box className={className + '__formGroup'}>
                                <Typography className={className + '__label'}>
                                    Full Name *
                                </Typography>
                                <TextField
                                    className={className + '__textField'}
                                    type="text"
                                    placeholder="Enter full name"
                                    value={reporterUser?.createdByFullName ?? ''}
                                    onChange={(e) => handleChangeValue(e, 'createdByFullName')}
                                    error={!!errors.createdByFullName}
                                    helperText={errors.createdByFullName}
                                    disabled={cursor === 'wait'}
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonIcon className={className + '__inputIcon'} />
                                            </InputAdornment>
                                        )
                                    }}
                                    required
                                />
                            </Box>

                            <Box className={className + '__formGroup'}>
                                <Typography className={className + '__label'}>
                                    Email Address *
                                </Typography>
                                <TextField
                                    className={className + '__textField'}
                                    type="email"
                                    placeholder="Enter email address"
                                    value={reporterUser?.createdByEmail ?? ''}
                                    onChange={(e) => handleChangeValue(e, 'createdByEmail')}
                                    error={!!errors.createdByEmail || !isValidMail}
                                    helperText={errors.createdByEmail || (!isValidMail ? 'Invalid email format' : '')}
                                    disabled={cursor === 'wait'}
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon className={className + '__inputIcon'} />
                                            </InputAdornment>
                                        )
                                    }}
                                    required
                                />
                            </Box>

                            <Box className={className + '__formGroup'}>
                                <Typography className={className + '__label'}>
                                    Contact Number *
                                </Typography>
                                <Box className={className + '__phoneInputWrapper'}>
                                    <PhoneIcon className={className + '__phoneIcon'} />
                                    <PhoneInput
                                        country="in"
                                        placeholder="Enter contact number"
                                        value={reporterUser?.createdByContactNumber ?? ''}
                                        specialLabel=""
                                        disabled={cursor === 'wait'}
                                        onChange={onPhoneNumberChange}
                                        containerClass={className + '__phoneContainer'}
                                        inputClass={className + '__phoneInput'}
                                        buttonClass={className + '__phoneButton'}
                                    />
                                </Box>
                                {errors.phone && (
                                    <Typography className={className + '__errorText'}>
                                        {errors.phone}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Divider />

                        {/* Footer */}
                        <Box className={className + '__footer'}>
                            <Button
                                variant="outlined"
                                onClick={props.closePopup}
                                className={className + '__cancelBtn'}
                                disabled={cursor === 'wait'}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={
                                    cursor === 'wait' ||
                                    !reporterUser?.createdByFullName?.trim() ||
                                    !reporterUser?.createdByContactNumber?.trim() ||
                                    !reporterUser?.createdByEmail?.trim() ||
                                    !isFieldUpdated
                                }
                                startIcon={<SaveIcon />}
                                className={className + '__submitBtn'}
                                type="submit"
                            >
                                Update Details
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </Fade>
        </Modal>
    );
}